import React from 'react';
import { Box, FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { MONO_FONT } from '../constants/typography';
import { SECRET_SENTINEL } from '../utils/registrySourceConfig';

// Campi di connessione per tipo di sorgente, usati da SourceConfigEditor. Sono funzioni di
// render (non componenti) perché `fieldProps` registra i percorsi resi durante il render del
// genitore: così gli errori senza campo finiscono nell'Alert in testa alla sezione.

const AUTH_TYPES = [
  { code: 'none', label: 'Nessuna' },
  { code: 'basic', label: 'Utente e password' },
  { code: 'bearer', label: 'Token bearer' },
  { code: 'api-key', label: 'Chiave API' },
];

const monoInput = { input: { sx: { fontFamily: MONO_FONT } } };
const gridSx = { display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' } };
const fullRow = { gridColumn: '1 / -1' };

const Field = ({ label, value, onChange, props, mono, ...rest }) => (
  <TextField
    size="small" fullWidth label={label}
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value)}
    slotProps={mono ? monoInput : undefined}
    {...props}
    {...rest}
  />
);

// Il backend conserva il segreto salvato solo se riceve la sentinella esatta: appena l'utente
// scrive, la sentinella sparisce e resta solo ciò che ha digitato (una cancellazione svuota).
const replaceSentinel = (previous, next) => {
  if (previous !== SECRET_SENTINEL || next === SECRET_SENTINEL) return next;
  // Testo inserito attorno alla sentinella non selezionata.
  if (next.includes(SECRET_SENTINEL)) return next.replace(SECRET_SENTINEL, '');
  // Parte della sentinella cancellata.
  if (/^\*+$/.test(next) && next.length < SECRET_SENTINEL.length) return '';
  // Sentinella selezionata e sostituita da ciò che si è digitato o incollato, asterischi compresi.
  return next;
};

const SecretField = ({ value, onChange, ...rest }) => (
  <Field
    type="password"
    value={value}
    onChange={(v) => onChange(replaceSentinel(value, v))}
    onFocus={(e) => { if (e.target.value === SECRET_SENTINEL) e.target.select(); }}
    {...rest}
  />
);

const csvFields = ({ source, setSource, fieldProps }) => (
  <Box sx={gridSx}>
    <Field
      label="Delimitatore" mono value={source.delimiter}
      onChange={(v) => setSource({ delimiter: v })}
      slotProps={{ htmlInput: { maxLength: 1 }, ...monoInput }}
      props={fieldProps('source.delimiter', 'Un solo carattere, di solito , oppure ;')}
    />
    <Field
      label="Codifica" mono value={source.charset}
      onChange={(v) => setSource({ charset: v })}
      props={fieldProps('source.charset', 'La codifica dei caratteri del file, di solito UTF-8')}
    />
  </Box>
);

const sapFields = ({ source, setSource, fieldProps }) => {
  const headers = source.headers || {};
  const setSapClient = (v) => {
    const next = { ...headers };
    if (v === '') delete next['sap-client'];
    else next['sap-client'] = v;
    setSource({ headers: next });
  };
  return (
    <Box sx={gridSx}>
      <Field
        label="URL del servizio" mono value={source.serviceUrl} sx={fullRow}
        onChange={(v) => setSource({ serviceUrl: v })}
        props={fieldProps('source.serviceUrl', 'L’indirizzo della entity set, es. https://…/API_PRODUCT_SRV/A_Product')}
      />
      <Field label="Utente" value={source.username} onChange={(v) => setSource({ username: v })}
        props={fieldProps('source.username')} />
      <SecretField label="Password" value={source.password} onChange={(v) => setSource({ password: v })}
        props={fieldProps('source.password')} />
      <Field label="Header sap-client" mono value={headers['sap-client']} onChange={setSapClient}
        props={fieldProps('source.headers', 'Il mandante SAP, es. 100')} />
    </Box>
  );
};

const authFields = (auth, setAuth, fieldProps) => {
  switch (auth.type) {
    case 'basic':
      return [
        <Field key="u" label="Utente" value={auth.username} onChange={(v) => setAuth({ username: v })}
          props={fieldProps('source.auth.username')} />,
        <SecretField key="p" label="Password" value={auth.password}
          onChange={(v) => setAuth({ password: v })} props={fieldProps('source.auth.password')} />,
      ];
    case 'bearer':
      return [
        <SecretField key="t" label="Token" value={auth.token} onChange={(v) => setAuth({ token: v })}
          props={fieldProps('source.auth.token')} />,
      ];
    case 'api-key':
      return [
        <Field key="h" label="Nome header" mono value={auth.headerName} onChange={(v) => setAuth({ headerName: v })}
          props={fieldProps('source.auth.headerName', 'es. X-Api-Key')} />,
        <SecretField key="k" label="Chiave" value={auth.apiKey} onChange={(v) => setAuth({ apiKey: v })}
          props={fieldProps('source.auth.apiKey')} />,
      ];
    default:
      return [];
  }
};

const crmRestFields = ({ source, setSource, fieldProps }) => {
  const auth = source.auth || { type: 'none' };
  const setAuth = (patch) => setSource({ auth: { ...auth, ...patch } });
  const authError = fieldProps('source.auth.type');
  return (
    <Box sx={gridSx}>
      <Field
        label="URL" mono value={source.url} sx={fullRow}
        onChange={(v) => setSource({ url: v })}
        props={fieldProps('source.url', 'L’indirizzo che restituisce la lista in JSON')}
      />
      <FormControl size="small" fullWidth error={authError.error}>
        <InputLabel id="source-auth-type-label">Autenticazione</InputLabel>
        <Select
          labelId="source-auth-type-label"
          label="Autenticazione"
          value={auth.type || 'none'}
          // Cambiando tipo si riparte dai soli campi del nuovo tipo.
          onChange={(e) => setSource({ auth: { type: e.target.value } })}
        >
          {AUTH_TYPES.map((a) => <MenuItem key={a.code} value={a.code}>{a.label}</MenuItem>)}
        </Select>
        {authError.error && <FormHelperText>{authError.helperText}</FormHelperText>}
      </FormControl>
      {authFields(auth, setAuth, fieldProps)}
      <Field
        label="Percorso degli elementi" mono value={source.itemsPath} sx={fullRow}
        onChange={(v) => setSource({ itemsPath: v })}
        props={fieldProps('source.itemsPath', 'Vuoto se la risposta è già una lista; altrimenti es. data.items')}
      />
    </Box>
  );
};

const RENDERERS = { csv: csvFields, sap: sapFields, 'crm-rest': crmRestFields };

/** Campi di connessione per `sourceType`, oppure null se il tipo non ne ha (vuoto o precedente). */
export const renderConnectionFields = (sourceType, args) => {
  const render = RENDERERS[sourceType];
  return render ? render(args) : null;
};
